/** Stereo delay effect controls.
 *
 * Provides UI for delay time (free ms or tempo-synced note divisions),
 * feedback, mix, ping-pong toggle, and lowpass filter cutoff.
 */

import { Clock, ToggleLeft, ToggleRight } from "lucide-react";
import { useCallback } from "react";
import type {
	DelayParams,
	DelayTimeMode,
	NoteDivision,
} from "../types/synth.ts";
import { NOTE_DIVISION_MULTIPLIERS } from "../types/synth.ts";

/** Props for the DelayControls component. */
export type DelayControlsProps = {
	readonly params: DelayParams;
	readonly tempo: number;
	readonly onChange: (update: Partial<DelayParams>) => void;
};

/** All available note divisions for the selector. */
const NOTE_DIVISIONS: readonly NoteDivision[] = Object.keys(
	NOTE_DIVISION_MULTIPLIERS,
) as NoteDivision[];

/** Format frequency for display. */
function formatFreq(hz: number): string {
	return hz >= 1000 ? `${(hz / 1000).toFixed(1)}kHz` : `${hz.toFixed(0)}Hz`;
}

/** Log mapping for filter cutoff slider. */
function linearToLog(value: number): number {
	const minLog = Math.log(200);
	const maxLog = Math.log(20000);
	return Math.exp(minLog + value * (maxLog - minLog));
}

function logToLinear(freq: number): number {
	const minLog = Math.log(200);
	const maxLog = Math.log(20000);
	return (Math.log(freq) - minLog) / (maxLog - minLog);
}

/** Calculate effective delay time in ms for display. */
function effectiveTimeMs(params: DelayParams, tempo: number): number {
	if (params.timeMode === "sync") {
		const beatDuration = 60 / tempo;
		return beatDuration * NOTE_DIVISION_MULTIPLIERS[params.noteDivision] * 1000;
	}
	return params.timeMs;
}

export function DelayControls({ params, tempo, onChange }: DelayControlsProps) {
	const handleToggleEnabled = useCallback(() => {
		onChange({ enabled: !params.enabled });
	}, [params.enabled, onChange]);

	const handleTimeModeToggle = useCallback(() => {
		const next: DelayTimeMode = params.timeMode === "free" ? "sync" : "free";
		onChange({ timeMode: next });
	}, [params.timeMode, onChange]);

	const handlePingPongToggle = useCallback(() => {
		onChange({ pingPong: !params.pingPong });
	}, [params.pingPong, onChange]);

	const delayMs = effectiveTimeMs(params, tempo);

	return (
		<div className="delay-controls">
			<div className="delay-header">
				<span className="control-label">
					<Clock size={14} />
					Delay
				</span>
				<button
					type="button"
					className={`delay-toggle ${params.enabled ? "active" : ""}`}
					onClick={handleToggleEnabled}
					title={params.enabled ? "Disable delay" : "Enable delay"}
				>
					{params.enabled ? (
						<ToggleRight size={16} />
					) : (
						<ToggleLeft size={16} />
					)}
				</button>
			</div>

			{params.enabled && (
				<div className="delay-params">
					{/* Time mode toggle */}
					<div className="delay-mode-row">
						<button
							type="button"
							className={`delay-mode-btn ${params.timeMode === "sync" ? "active" : ""}`}
							onClick={handleTimeModeToggle}
							title={
								params.timeMode === "sync"
									? "Switch to free (ms)"
									: "Switch to tempo sync"
							}
						>
							{params.timeMode === "sync" ? "Sync" : "Free"}
						</button>
						<span className="delay-time-display">{Math.round(delayMs)}ms</span>
					</div>

					{/* Time control */}
					{params.timeMode === "free" ? (
						<div className="delay-param-row">
							<span className="delay-param-label">Time</span>
							<input
								type="range"
								min={10}
								max={2000}
								step={1}
								value={params.timeMs}
								onChange={(e) => onChange({ timeMs: Number(e.target.value) })}
								className="delay-param-slider"
								aria-label="Delay time"
							/>
							<span className="delay-param-value">{params.timeMs}ms</span>
						</div>
					) : (
						<div className="delay-note-grid">
							{NOTE_DIVISIONS.map((div) => (
								<button
									key={div}
									type="button"
									className={`delay-note-btn ${params.noteDivision === div ? "active" : ""}`}
									onClick={() => onChange({ noteDivision: div })}
									title={div}
								>
									{div}
								</button>
							))}
						</div>
					)}

					{/* Feedback */}
					<div className="delay-param-row">
						<span className="delay-param-label">Fdbk</span>
						<input
							type="range"
							min={0}
							max={0.95}
							step={0.01}
							value={params.feedback}
							onChange={(e) => onChange({ feedback: Number(e.target.value) })}
							className="delay-param-slider"
							aria-label="Delay feedback"
						/>
						<span className="delay-param-value">
							{Math.round(params.feedback * 100)}%
						</span>
					</div>

					{/* Mix */}
					<div className="delay-param-row">
						<span className="delay-param-label">Mix</span>
						<input
							type="range"
							min={0}
							max={1}
							step={0.01}
							value={params.mix}
							onChange={(e) => onChange({ mix: Number(e.target.value) })}
							className="delay-param-slider"
							aria-label="Delay mix"
						/>
						<span className="delay-param-value">
							{Math.round(params.mix * 100)}%
						</span>
					</div>

					{/* Filter cutoff */}
					<div className="delay-param-row">
						<span className="delay-param-label">LP</span>
						<input
							type="range"
							min={0}
							max={1}
							step={0.001}
							value={logToLinear(params.filterCutoff)}
							onChange={(e) =>
								onChange({
									filterCutoff: linearToLog(Number(e.target.value)),
								})
							}
							className="delay-param-slider"
							aria-label="Delay filter cutoff"
						/>
						<span className="delay-param-value">
							{formatFreq(params.filterCutoff)}
						</span>
					</div>

					{/* Ping-pong toggle */}
					<div className="delay-param-row">
						<span className="delay-param-label">P-P</span>
						<button
							type="button"
							className={`delay-pp-btn ${params.pingPong ? "active" : ""}`}
							onClick={handlePingPongToggle}
							title={params.pingPong ? "Ping-pong: ON" : "Ping-pong: OFF"}
						>
							{params.pingPong ? "ON" : "OFF"}
						</button>
					</div>
				</div>
			)}
		</div>
	);
}
